import loadable from "@loadable/component";
import React from "react";
import ContinueToOption from "web/react/components/continue-to-option/continue-to-option";
import { CampaignDetails } from "web/react/hooks/use-web-to-app-campaign/use-web-to-app-campaign";
import { gettext, gettextNoop } from "web/script/modules/django-i18n";
import history from "web/script/utils/history";

const SendToAppChecklistModal = loadable(
    () =>
        import(
            /* webpackChunkName: "send-to-app-checklist-modal" */ "web/react/components/send-to-app-checklist-modal/send-to-app-checklist-modal"
        )
);

const SendToAppModal = loadable(
    () =>
        import(
            /* webpackChunkName: "send-to-app-modal" */ "web/react/components/send-to-app-modal/send-to-app-modal"
        )
);

// must add all usable titles to this array so they are included in the bundled translations
const TITLE_KEYS = [
    gettextNoop("feed.show_more.app_overlay_block.title_4"),
    gettextNoop("product_card.app_overlay_block.title_1"),
    gettextNoop("in_stock_product.related_show_more.app_overlay_block.title_1"),
    gettextNoop("oos_product.related_show_more.app_overlay_block.title_2"),
    gettextNoop("is_product.shop_now.app_overlay_block.title_1"),
];

interface WebToAppCampaignModalProps {
    id: number | string;
    isOpen: boolean;
    onClose: () => void;
    onCtaClick: () => void;
    analyticsEventLabel: string;
    campaignDetails?: CampaignDetails;
    continueOptionHref?: string;
    onContinueClick?: (event) => void;
    productImageUrl?: string;
}

export default function WebToAppCampaignModal({
    id,
    isOpen,
    onClose,
    campaignDetails,
    continueOptionHref,
    onContinueClick,
    onCtaClick,
    analyticsEventLabel,
    productImageUrl,
}: WebToAppCampaignModalProps): JSX.Element {
    // WebToAppCampaigns with the same `analyticsEventLabel` require an `id` to avoid mismatched overlays
    const modalName = `modal-${analyticsEventLabel}-${id}`;
    const { branchLink, componentType, blockType } = campaignDetails || {};
    const titleKey = campaignDetails?.titleKey || "";
    if (titleKey && !TITLE_KEYS.includes(titleKey)) {
        console.error("Invalid titleKey");
    }

    function onAfterOpen(): void {
        // check overlay state to avoid delay caused by hooks
        if (history.getCurrentState().overlay !== modalName) {
            history.pushOverlay(modalName);
        }
    }

    function onModalClose(): void {
        history.popOverlay(modalName);
        onClose();
    }

    let continueOption;
    if (blockType === "soft_block" && (onContinueClick || continueOptionHref)) {
        continueOption = (
            <ContinueToOption
                href={continueOptionHref}
                continueMessage={gettext("general.continue_shopping_btn_text")}
                analyticsEventLabel={analyticsEventLabel}
                continueClick={(event) => {
                    onContinueClick?.(event);
                    onClose();
                }}
            />
        );
    }

    const extraContent = continueOption ? () => continueOption : undefined;

    return (
        <>
            {componentType === "checklist_modal" && (
                <SendToAppChecklistModal
                    url={branchLink}
                    isOpen={isOpen}
                    onClose={onModalClose}
                    analyticsEventLabel={analyticsEventLabel}
                    onCtaClick={onCtaClick}
                />
            )}
            {componentType === "generic_modal" && (
                <SendToAppModal
                    title={gettext(titleKey)}
                    url={branchLink}
                    isOpen={isOpen}
                    onClose={onModalClose}
                    analyticsEventLabel={analyticsEventLabel}
                    onCtaClick={onCtaClick}
                    productImageURL={productImageUrl}
                    extraContent={extraContent}
                    onAfterOpen={onAfterOpen}
                />
            )}
        </>
    );
}
